<template>
  <div v-if="state.loading" class='flex h-full items-center justify-center'>
    <div>
      {{ $t('cart.init') }}
    </div>
  </div>

  <div v-else-if="cartItems.length === 0" class="flex flex-col justify-center items-center h-full xs:py-10 md:py-30  xs:mx-2 md:mx-20">
    <p class='font-medium xs:text-24px sm:text-32px'>
      {{ $t('cart.empty') }}
    </p>
    <p class='xs:w-[234px] sm:w-auto text-center mt-1.5 font-normal text-gray-10 text-16px'>
      {{ $t('campaign_cart.index.empty_content') }}
    </p>
    <img src="../../images/cart-empty.svg" class='inline-block max-w-[84px] max-h-[84px] mb-8 mt-6' />
    <div class='continue-shopping-btn w-fit sm:w-auto inline-flex items-center btn rounded-full text-[14px] text-gray-100 mt-2 sm:mt-0 py-1.5 px-[12px] border border-gray-100 hover:bg-gray-500'>
      <div class="mr-1">
        {{ $t('campaign_cart.index.continue_shopping') }}
      </div>
      <img src="../../images/icons/back-page.svg" class='h-4 w-4 inline-block opacity-70 rotate-180' />
    </div>
  </div>

  <div
    v-else
    class="flex flex-col md:grid md:grid-cols-[1fr_300px] lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_416px] grid-rows-[auto_1fr] grid-flow-col max-w-[1280px] md:h-full mx-auto gap-x-4 px-4 py-[84px]"
  >
    <div class="shopping-cart">
      <h1 class="text-24px font-bold leading-8">
        {{ $t('campaign_cart.index.your_contribution') }}
      </h1>

      <div class="w-full flex flex-col">
        <template v-for="cartItem in cartItems" :key="cartItem.id">
          <hr class="my-4 border-gray-160">
          <div class="grid grid-cols-[1fr_auto] grid-rows-[auto_1fr_auto] gap-x-4 gap-y-2 bg-gray-500 rounded-lg p-4">
            <div class="w-[160px] lg:w-[200px] xl:w-[238px] aspect-square row-span-2 lg:row-span-3">
              <a :href="`/campaigns/${state.campaign.id}`">
                <img
                    :src="cartItem.reward?.cover_image"
                    :alt="cartItem.reward?.title"
                    class="w-full h-full object-cover rounded-lg"
                  />
              </a>
            </div>
            <div class="base-info flex flex-col justify-between gap-y-2">
              <div class="text-18px leading-6 font-bold" :title="cartItem.reward?.title">
                {{ cartItem.reward?.title }}
              </div>
              <div class="text-14px leading-5 text-gray-150 line-clamp-3 xl:line-clamp-5" :title="cartItem.reward?.description">
                {{ cartItem.reward?.description }}
              </div>
            </div>
            <div class="flex items-center gap-x-8 lg:gap-x-[100px]">
              <div v-if="cartItem.reward?.sold_quantity > 0" class="hidden sm:flex flex-col gap-y-1">
                <div class="text-12px leading-4 text-gray-90 font-bold">
                  {{ $t('campaign_cart.index.backers') }}
                </div>
                <div class="text-16px leading-5 text-gray-90">
                  {{ cartItem.reward?.sold_quantity }}
                </div>
              </div>
              <div v-if="cartItem.reward?.estimated_delivery_at" class="flex flex-col gap-y-1">
                <div class="text-12px leading-4 text-gray-90 font-bold">
                  {{ $t('campaign_cart.index.estimated_delivery') }}
                </div>
                <div class="text-16px leading-5 text-gray-90">
                  {{ formatDate(cartItem.reward?.estimated_delivery_at) }}
                </div>
              </div>
            </div>
            <div class="item-info flex justify-between items-center col-span-2 lg:col-span-1 mt-2 lg:mt-0">
              <div class="text-20px leading-6 font-medium">
                <Currency :price="cartItem.unit_price" />
              </div>
              <div class="w-[140px] lg:w-[200px] p-2 flex items-center justify-between text-16px leading-6 font-medium border border-gray-90 rounded-xl">
                <button
                  type="button"
                  class="flex items-center justify-center"
                  :class="{ 'opacity-30 cursor-not-allowed': cartItem.quantity === 0 }"
                  :disabled="cartItem.quantity === 0"
                  @click="handleDecrease(cartItem.reward)"
                  aria-label="remove from cart">
                    <img src="../../images/icons/checkbox_remove.svg" class="h-6 w-6" alt="remove">
                </button>
                <div class="text-16px font-bold">
                  {{ cartItem.quantity }}
                </div>
                <button
                  type="button"
                  class="flex items-center justify-center"
                  @click="handleIncrease(cartItem.reward)"
                  aria-label="add to cart">
                  <img src="../../images/icons/checkbox_add.svg" class="h-6 w-6" alt="add">
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="row-span-2 mt-6 md:mt-0">
      <div class="p-4 bg-gray-500 rounded-xl">
        <div v-if="requiresShippingAddress" class="text-16px leading-6 font-bold mb-2"> {{ $t('campaign_cart.index.shipping_info') }} </div>
        <form
          ref="shippingFormRef"
          :action="checkoutUrl"
          method="post"
          @submit.prevent="handleSubmit"
          @focusout="handleShippingFormFocusOut"
        >
          <input type="hidden" name="authenticity_token" :value="csrfToken">

          <div v-if="requiresShippingAddress" class="grid gap-2">
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-2">
              <div class="name">
                <label class="block text-14px leading-5 font-bold mb-2">
                  {{ $t('campaign_cart.index.contact_name') }} <span class="text-orange-300">*</span>
                </label>
                <input
                  type="text"
                  name="shipping_info[name]"
                  v-model="form.name"
                  @blur="handleShippingInfoBlur('name')"
                  class="w-full default-input-klass !border-gray-150"
                  required
                  autocomplete="name"
                >
              </div>

              <div class="phone">
                <label class="block text-14px leading-5 font-bold mb-2">
                  {{ $t('campaign_cart.index.phone') }} <span class="text-orange-300">*</span>
                </label>
                <input
                  type="tel"
                  name="shipping_info[phone]"
                  v-model="form.phone"
                  @blur="handleShippingInfoBlur('phone')"
                  class="w-full default-input-klass !border-gray-150"
                  required
                  autocomplete="tel"
                >
              </div>
            </div>

            <div class="country_code">
              <label class="block text-14px leading-5 font-bold mb-2">
                {{ $t('campaign_cart.index.country_region') }} <span class="text-orange-300">*</span>
              </label>
              <select
                name="shipping_info[country_code]"
                v-model="form.country_code"
                class="default-input-klass !border-gray-150"
                data-uniform-select
                data-searchable
                required
              >
                <option value="" disabled>Select Country</option>
                <option
                  v-for="option in normalizedCountryOptions"
                  :key="option[1]"
                  :value="option[1]"
                >
                  {{ option[0] }}
                </option>
              </select>
            </div>

            <div class="zip">
              <label class="block text-14px leading-5 font-bold mb-2">
                {{ $t('campaign_cart.index.zip_postal_code') }} <span class="text-orange-300">*</span>
              </label>
              <input
                type="text"
                name="shipping_info[zip]"
                v-model="form.zip"
                @blur="handleShippingInfoBlur('zip')"
                class="w-full default-input-klass !border-gray-150"
                required
                autocomplete="postal-code"
              >
            </div>

            <div class="state">
              <label class="block text-14px leading-5 font-bold mb-2">
                {{ $t('campaign_cart.index.state_province_region') }} <span class="text-orange-300">*</span>
              </label>
              <input
                type="text"
                name="shipping_info[state]"
                v-model="form.state"
                @blur="handleShippingInfoBlur('state')"
                class="w-full default-input-klass !border-gray-150"
                required
                autocomplete="address-level1"
              >
            </div>

            <div class="city">
              <label class="block text-14px leading-5 font-bold mb-2">
                {{ $t('campaign_cart.index.town_city') }} <span class="text-orange-300">*</span>
              </label>
              <input
                type="text"
                name="shipping_info[city]"
                v-model="form.city"
                @blur="handleShippingInfoBlur('city')"
                class="w-full default-input-klass !border-gray-150"
                required
                autocomplete="address-level2"
              >
            </div>

            <div class="address1">
              <label class="block text-14px leading-5 font-bold mb-2">
                {{ $t('campaign_cart.index.address_line_1') }} <span class="text-orange-300">*</span>
              </label>
              <input
                type="text"
                name="shipping_info[address1]"
                v-model="form.address1"
                @blur="handleShippingInfoBlur('address1')"
                class="w-full default-input-klass !border-gray-150"
                required
                autocomplete="address-line1"
              >
            </div>

            <div class="address2">
              <label class="block text-14px leading-5 font-bold mb-2">
                {{ $t('campaign_cart.index.address_line_2') }}
              </label>
              <input
                type="text"
                name="shipping_info[address2]"
                v-model="form.address2"
                @blur="handleShippingInfoBlur('address2')"
                class="w-full default-input-klass !border-gray-150"
                autocomplete="address-line2"
              >
            </div>

            <hr class="border-gray-200 my-6">
          </div>

          <template v-if="cartItems.length > 0">
            <div class="text-16px leading-5 font-bold mb-4">
              {{ $t('campaign_cart.index.pledge_amount') }}
            </div>
            <div class="flex flex-col gap-y-2">
              <div v-for="cartItem in cartItems" :key="cartItem.id"
                class="flex flex-col">
                <div class="flex justify-between text-14px leading-6 text-gray-150">
                  <span class="line-clamp-1 flex-1" :title="cartItem.reward.title">{{ cartItem.reward.title }}</span>
                  <Currency :price="cartItem.line_price" />
                </div>

                <div class="flex justify-between text-12px leading-5" :class="isItemUndeliverable(cartItem) ? 'text-red-500' : 'text-gray-150'">
                  <span>{{ $t('campaign_cart.index.shipping_fee') }} x {{ cartItem.quantity }}</span>
                  <span v-if="isItemUndeliverable(cartItem)" class="font-medium">{{ $t('campaign_cart.index.undeliverable') }}</span>
                  <span v-else-if="cartItem.shipping_fee == null" class="text-gray-150">{{ $t('campaign_cart.index.unknown_shipping_fee') }}</span>
                  <Currency v-else :price="cartItem.shipping_fee" />
                </div>
              </div>
            </div>

            <hr class="border-gray-200 my-4">

            <div class="flex justify-between text-18px leading-6 font-bold">
              <span>{{ $t('campaign_cart.index.total') }}</span>
              <Currency :price="total" />
            </div>

            <hr class="border-gray-200 my-4">
          </template>

          <div v-if="hasUndeliverableItems" class="text-red-500 text-12px leading-4 mb-2">
            {{ $t('campaign_cart.index.undeliverable_warning') }}
          </div>

          <button
            type="submit"
            class="w-full btn stripe_gw_btn h-12 text-16px"
            :class="{ 'opacity-60 cursor-not-allowed': state.submitting || hasUndeliverableItems }"
            :disabled="state.submitting || hasUndeliverableItems"
          >
            {{ state.submitting ? $t('campaign_cart.index.checking') : $t('campaign_cart.index.pay_with_gateway', { gateway: gatewayProvider }) }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, computed, onMounted, onUpdated, ref, watch } from 'vue';

export default defineComponent({
  name: 'CampaignCartIndex',
  setup() {
    const FETCH_URL = '/api/v1/campaign_cart/current';
    const CHECKOUT_URL = '/campaigns/cart/checkout';
    const SHIPPING_INFO_URL = '/api/v1/campaign_cart/shipping_info';

    const state = reactive({
      loading: true,
      submitting: false,
      cart: null,
      campaign: null,
      countryOptions: [],
      shippingInfo: null,
      error: '',
      shippingInfoUpdating: false,
    });

    const form = reactive({
      name: '',
      phone: '',
      country_code: '',
      zip: '',
      state: '',
      city: '',
      address1: '',
      address2: ''
    });

    const fillShippingForm = (shippingInfo) => {
      const info = shippingInfo || {};

      form.name = info.name || '';
      form.phone = info.phone || '';
      form.country_code = info.country_code || '';
      form.zip = info.zip || '';
      form.state = info.state || '';
      form.city = info.city || '';
      form.address1 = info.address1 || '';
      form.address2 = info.address2 || '';
    };

    const csrfToken = ref(document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '');

    let pendingShippingChanges = {};
    let shippingPersistPromise = null;
    const shippingFormRef = ref(null);
    const normalizeValue = (value) => (value == null ? '' : value);
    const hasPendingShippingChanges = () => Object.keys(pendingShippingChanges).length > 0;
    const hasPendingField = (field) => Object.prototype.hasOwnProperty.call(pendingShippingChanges, field);

    const normalizedCountryOptions = computed(() => {
      return Array.isArray(state.countryOptions) ? state.countryOptions : [];
    });

    const cartItems = computed(() => state.cart?.items || []);
    const gatewayProvider = computed(() => state.cart?.gateway_provider || 'Stripe');

    const subtotal = computed(() => {
      if (state.cart?.subtotal != null) {
        return Number(state.cart.subtotal);
      }

      return cartItems.value.reduce((sum, item) => {
        if (item?.line_price != null) {
          return sum + Number(item.line_price);
        }

        const unit = Number(item?.unit_price || 0);
        const quantity = Number(item?.quantity || 0);
        return sum + unit * quantity;
      }, 0);
    });

    const shippingFee = computed(() => state.cart?.items?.reduce((sum, item) => sum + Number(item.shipping_fee || 0), 0) || 0);

    const requiresShippingAddress = computed(() => state.cart?.requires_shipping_address ?? true);

    const hasPersistedShippingCountry = computed(() => !!state.shippingInfo?.country_code);

    const isItemUndeliverable = (item) => hasPersistedShippingCountry.value && item?.shipping_fee == null;

    const hasUndeliverableItems = computed(() => {
      if (!requiresShippingAddress.value) return false;
      if (!hasPersistedShippingCountry.value) return false;
      return cartItems.value.some(item => item.shipping_fee == null);
    });

    const total = computed(() => subtotal.value + shippingFee.value);

    const fetchCart = async () => {
      try {
        const response = await fetch(FETCH_URL, { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        state.cart = data.cart || null;
        state.campaign = data.campaign || null;
        state.countryOptions = data.country_options || [];
        state.shippingInfo = data.cart?.shipping_info || null;

        fillShippingForm(state.shippingInfo);
      } catch (error) {
        console.error('Failed to load campaign cart data:', error);
      } finally {
        state.loading = false;
      }
    };

    const persistShippingInfo = async (payload) => {
      if (!payload || Object.keys(payload).length === 0) { return; }

      state.shippingInfoUpdating = true;

      try {
        const response = await fetch(SHIPPING_INFO_URL, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken.value
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data?.cart) {
          state.cart = data.cart || null;
          state.shippingInfo = data.cart?.shipping_info || null;
          fillShippingForm(state.shippingInfo);
        }
      } catch (error) {
        console.error('Failed to update shipping info:', error);
      } finally {
        state.shippingInfoUpdating = false;
      }
    };

    const flushPendingShippingChanges = async () => {
      if (shippingPersistPromise) {
        await shippingPersistPromise;
      }

      while (hasPendingShippingChanges()) {
        const payload = { ...pendingShippingChanges };
        pendingShippingChanges = {};

        shippingPersistPromise = persistShippingInfo(payload).finally(() => {
          shippingPersistPromise = null;
        });

        await shippingPersistPromise;
      }
    };

    const handleShippingInfoBlur = (field) => {
      if (!field) { return; }

      const newValue = normalizeValue(form[field]);
      const existingValue = normalizeValue(state.shippingInfo?.[field]);
      const hasPendingValue = hasPendingField(field);
      const pendingValue = hasPendingValue ? normalizeValue(pendingShippingChanges[field]) : existingValue;

      if (newValue === existingValue) {
        if (hasPendingValue) {
          delete pendingShippingChanges[field];
        }
        return;
      }

      if (newValue === pendingValue) {
        return;
      }

      pendingShippingChanges[field] = newValue;
    };

    // Tom Select + v-model：flush: 'post' 確保選完後 form.country_code 已同步再送出；
    // 國家一改運費／cart 需立即重算，不等 focusout。
    watch(
      () => form.country_code,
      async () => {
        if (state.loading) return;
        handleShippingInfoBlur('country_code');
        await flushPendingShippingChanges();
      },
      { flush: 'post' }
    );

    const handleShippingFormFocusOut = async (event) => {
      const nextTarget = event.relatedTarget;
      const formEl = shippingFormRef.value;

      if (formEl && nextTarget && formEl.contains(nextTarget)) {
        return;
      }

      await flushPendingShippingChanges();
    };

    const formatDate = (value) => {
      if (!value) { return ''; }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) { return value; }
      return date.toLocaleDateString();
    };

    const updateRewardQuantity = async (rewardId, method) => {
      if (!rewardId) { return; }

      try {
        const response = await fetch(`/api/v1/campaign_cart/rewards/${rewardId}`, {
          method,
          credentials: 'include',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        state.cart = data.cart || null;
      } catch (error) {
        console.error('Failed to update campaign cart item:', error);
      }
    };

    const handleIncrease = (reward) => {
      const rewardId = reward?.id;
      if (!rewardId) { return; }
      updateRewardQuantity(rewardId, 'POST');
    };

    const handleDecrease = (reward) => {
      const rewardId = reward?.id;
      if (!rewardId) { return; }
      updateRewardQuantity(rewardId, 'DELETE');
    };

    const requiredFields = ['name', 'phone', 'country_code', 'zip', 'state', 'city', 'address1'];

    const validateForm = () => {
      if (!requiresShippingAddress.value) return true;

      return requiredFields.every((field) => {
        const value = form[field];
        return value !== null && value !== undefined && value.toString().trim().length > 0;
      });
    };

    const handleSubmit = async (event) => {
      state.error = '';

      if (!validateForm()) {
        state.error = '請完整填寫必填欄位。';
        return;
      }

      state.submitting = true;
      if (requiresShippingAddress.value) {
        await flushPendingShippingChanges();
      }
      event.target.submit();
    };

    onMounted(fetchCart);

    onUpdated(async () => {
      window.initUniformSelect()
    });

    return {
      state,
      form,
      csrfToken,
      normalizedCountryOptions,
      cartItems,
      gatewayProvider,
      shippingFee,
      requiresShippingAddress,
      hasUndeliverableItems,
      isItemUndeliverable,
      total,
      formatDate,
      handleSubmit,
      handleIncrease,
      handleDecrease,
      handleShippingInfoBlur,
      handleShippingFormFocusOut,
      shippingFormRef,
      checkoutUrl: CHECKOUT_URL
    };
  }
});
</script>
